import { useCallback, useEffect, useMemo } from 'react';
import { useHubStore, type HubRpcAdminOptions } from '../stores/hubStore';
import type { HubInteraction } from '../types/hub';

export interface HubParticipantDescriptor {
  /** Unique key for this participant thread (e.g. quote_id or synthetic key). */
  participantKey: string;
  /** Backend quote/thread identifier (may be null for job-level threads). */
  quoteId: string | null;
  /** Other participant's profile_id for this thread. */
  participantId: string;
}

interface UseHubInteractionsByParticipantResult {
  getInteractions: (participantKey: string) => HubInteraction[];
  getLoading: (participantKey: string) => boolean;
  getLoadingMore: (participantKey: string) => boolean;
  getHasMore: (participantKey: string) => boolean;
  /**
   * Returns a callback that will load more interactions for the given participant when invoked.
   */
  loadMore: (participantKey: string) => () => void;
  /**
   * Returns a callback that will refresh the interactions for the given participant when invoked.
   */
  refresh: (participantKey: string) => () => void;
  /**
   * Immediately refresh interactions for the given participant key.
   * Intended for use with polling when new unread counts are detected.
   */
  refreshParticipantNow: (participantKey: string) => void;
}

/**
 * @param currentParticipantKey - When set, only this thread's interactions are fetched (on demand).
 * Pass the selected participant's key so we make one call instead of one per participant.
 * @param adminEffectiveViewerId - Admin hub: load threads as this profile (see hub RPC migrations).
 */
export function useHubInteractionsByParticipant(
  jobId: string | undefined,
  participants: HubParticipantDescriptor[],
  currentParticipantKey?: string | null,
  adminEffectiveViewerId?: string | null
): UseHubInteractionsByParticipantResult {
  const {
    interactionsByParticipantKey,
    loadingByParticipantKey,
    loadingMoreByParticipantKey,
    hasMoreByParticipantKey,
    fetchHubInteractions,
    loadMoreInteractions,
    resetInteractions,
  } = useHubStore();

  const rpcAdminOptions: HubRpcAdminOptions = useMemo(
    () => ({ adminEffectiveViewerId: adminEffectiveViewerId ?? null }),
    [adminEffectiveViewerId]
  );

  const participantMap = useMemo(() => {
    const deduped = new Map<
      string,
      {
        quoteId: string | null;
        participantId: string;
      }
    >();

    for (const p of participants) {
      if (!p.participantKey || !p.participantId) continue;
      if (!deduped.has(p.participantKey)) {
        deduped.set(p.participantKey, {
          quoteId: p.quoteId ?? null,
          participantId: p.participantId,
        });
      }
    }

    return deduped;
  }, [participants]);

  // Reset all interactions when the job or admin viewer changes.
  useEffect(() => {
    if (!jobId) {
      resetInteractions();
      return;
    }
    resetInteractions();
  }, [jobId, adminEffectiveViewerId, resetInteractions]);

  // Fetch interactions only for the current participant (lazy-load per thread to avoid N calls).
  // Skip if already loading for this participant to avoid duplicate get_hub_interactions calls.
  useEffect(() => {
    if (!jobId || !currentParticipantKey) return;

    const entry = participantMap.get(currentParticipantKey);
    if (!entry) return;

    const isLoading = loadingByParticipantKey[currentParticipantKey] ?? false;
    const hasInteractions = Boolean(interactionsByParticipantKey[currentParticipantKey]);
    if (isLoading || hasInteractions) return;

    fetchHubInteractions(
      jobId,
      currentParticipantKey,
      entry.participantId,
      null,
      rpcAdminOptions
    ).catch(() => {
      // Error is stored in hubStore; no-op here.
    });
  }, [
    jobId,
    currentParticipantKey,
    participantMap,
    interactionsByParticipantKey,
    loadingByParticipantKey,
    fetchHubInteractions,
    rpcAdminOptions,
  ]);

  const getInteractions = useCallback(
    (participantKey: string): HubInteraction[] => {
      const list = interactionsByParticipantKey[participantKey] ?? [];
      // Sort by created_at ascending (oldest first) so the feed is always ordered correctly.
      return [...list].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    },
    [interactionsByParticipantKey]
  );

  const getLoading = useCallback(
    (participantKey: string): boolean => loadingByParticipantKey[participantKey] ?? false,
    [loadingByParticipantKey]
  );

  const getLoadingMore = useCallback(
    (participantKey: string): boolean => loadingMoreByParticipantKey[participantKey] ?? false,
    [loadingMoreByParticipantKey]
  );

  const getHasMore = useCallback(
    (participantKey: string): boolean => hasMoreByParticipantKey[participantKey] ?? true,
    [hasMoreByParticipantKey]
  );

  const loadMore = useCallback(
    (participantKey: string) => {
      const entry = participantMap.get(participantKey);
      const participantId = entry?.participantId;
      return () => {
        if (!jobId || !participantId) return;
        loadMoreInteractions(jobId, participantKey, participantId, rpcAdminOptions);
      };
    },
    [jobId, loadMoreInteractions, participantMap, rpcAdminOptions]
  );

  const refresh = useCallback(
    (participantKey: string) => {
      const entry = participantMap.get(participantKey);
      const participantId = entry?.participantId;
      return () => {
        if (!jobId || !participantId) return;
        resetInteractions(participantKey);
        fetchHubInteractions(jobId, participantKey, participantId, null, rpcAdminOptions).catch(
          () => {}
        );
      };
    },
    [jobId, fetchHubInteractions, resetInteractions, participantMap, rpcAdminOptions]
  );

  const refreshParticipantNow = useCallback(
    (participantKey: string) => {
      const entry = participantMap.get(participantKey);
      const participantId = entry?.participantId;
      if (!jobId || !participantId) return;
      resetInteractions(participantKey);
      fetchHubInteractions(jobId, participantKey, participantId, null, rpcAdminOptions).catch(
        () => {}
      );
    },
    [jobId, fetchHubInteractions, resetInteractions, participantMap, rpcAdminOptions]
  );

  return {
    getInteractions,
    getLoading,
    getLoadingMore,
    getHasMore,
    loadMore,
    refresh,
    refreshParticipantNow,
  };
}
